/**
 * As regras puras da tela de manutenções: formatos, busca, listas dos filtros.
 *
 * Papel: aqui fica o que as três abas fazem igual à origem e que não é desenho —
 * a data vazia em branco (`fmtDate`, l. 784–789), o custo "R$ 1.234,50"
 * (`fmtMoney`, l. 790–795), a busca em minúsculas com `includes` (l. 929, 986,
 * 1021), as áreas e responsáveis distintos (`populateAreaFilters`, l. 962–970),
 * a próxima visita vencida (l. 1030) e o "#" como número (l. 873).
 *
 * Como usar: as abas filtram com `passaNaBusca` e com os `valorDa*` contra o que
 * a `MultiSelecao` tem marcado, e só então ordenam. A corretiva sem área e a sem
 * responsável entram nas multiseleções pelos valores `SEM_AREA` e
 * `SEM_RESPONSAVEL`, que só aparecem como opção quando alguma corretiva da casa
 * está assim — sem a opção, a corretiva sem área sumiria de toda tabela, como
 * sumia na origem.
 *
 * As listas são ordenadas por `localeCompare('pt-BR')`, e não pelo `.sort()` da
 * origem, que manda "Área externa" para depois de "Varanda".
 */
import { type Corretiva } from '@concierge/shared';

import { formatarData } from '@/shared/lib/formato';
import { type SituacaoDaLinhaVazia } from './components/kit/tabela';
import { type SituacaoDaLista } from './types';

/** A opção da multiseleção Área que recorta as corretivas sem área. */
export const SEM_AREA = '(Sem área)';

/** A opção da multiseleção Responsável que recorta as corretivas sem responsável. */
export const SEM_RESPONSAVEL = '(Sem responsável)';

/** As sugestões do `datalist` de periodicidade, na ordem da origem (l. 710–713). */
export const PERIODICIDADES_SUGERIDAS: readonly string[] = [
  'Diariamente',
  'Semanal',
  'Mensal',
  'Bimestral',
  'Trimestral',
  'Semestral',
  'Anual',
  'Bienal',
  'Quadrienal',
];

/** O formato do custo: duas casas e milhar, em pt-BR. */
const FORMATO_DO_CUSTO = new Intl.NumberFormat('pt-BR', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/**
 * Uma data da tabela.
 *
 * @param iso - `AAAA-MM-DD` ou `null`.
 * @returns `dd/mm/aaaa`, ou texto vazio — a origem não põe travessão.
 */
export function formatarDataDaTabela(iso: string | null): string {
  return iso === null ? '' : formatarData(iso);
}

/**
 * Um custo da tabela.
 *
 * O espaço depois de "R$" é o comum, como na origem, e não o não separável que
 * o `Intl` de moeda poria.
 *
 * @param custo - O custo, ou `null` quando não há valor lançado.
 * @returns "R$ 1.234,50"; "R$ 0,00" para zero; texto vazio para `null`.
 */
export function formatarCustoDaTabela(custo: number | null): string {
  return custo === null ? '' : `R$ ${FORMATO_DO_CUSTO.format(custo)}`;
}

/**
 * Diz se um registro passa na busca.
 *
 * A busca vai em minúsculas contra os campos em minúsculas, com `includes`: sem
 * distinção de maiúscula, com distinção de acento ("agua" não acha "Água"), e
 * sem `trim()` — um espaço digitado conta. Vazia, não filtra.
 *
 * @param busca - O texto digitado, como está no campo.
 * @param campos - Os campos em que a aba procura; `null` não acha nada.
 * @returns `true` quando a busca está vazia ou algum campo a contém.
 */
export function passaNaBusca(busca: string, campos: readonly (string | null)[]): boolean {
  if (busca === '') return true;
  const termo = busca.toLowerCase();
  return campos.some((campo) => (campo ?? '').toLowerCase().includes(termo));
}

/**
 * Os textos distintos e não vazios, em ordem alfabética do português.
 *
 * @param valores - Os textos de uma coluna, com os nulos.
 * @returns A lista sem repetição, sem vazio e ordenada; é a do `datalist`.
 */
export function distintosOrdenados(valores: readonly (string | null)[]): string[] {
  return [
    ...new Set(valores.filter((valor): valor is string => valor !== null && valor !== '')),
  ].sort((um, outro) => um.localeCompare(outro, 'pt-BR'));
}

/**
 * As opções da multiseleção Área.
 *
 * @param corretivas - As corretivas da casa, sem filtro nenhum.
 * @returns As áreas distintas e, no fim, `SEM_AREA` se alguma não tem área.
 */
export function opcoesDeArea(corretivas: readonly Corretiva[]): string[] {
  const areas = distintosOrdenados(corretivas.map((corretiva) => corretiva.area));
  return corretivas.some((corretiva) => corretiva.area === null)
    ? [...areas, SEM_AREA]
    : areas;
}

/**
 * As opções da multiseleção Responsável.
 *
 * @param corretivas - As corretivas da casa, sem filtro nenhum.
 * @returns Os responsáveis distintos e, no fim, `SEM_RESPONSAVEL` se falta em
 *   alguma.
 */
export function opcoesDeResponsavel(corretivas: readonly Corretiva[]): string[] {
  const nomes = distintosOrdenados(corretivas.map((corretiva) => corretiva.responsavel));
  return corretivas.some((corretiva) => corretiva.responsavel === null)
    ? [...nomes, SEM_RESPONSAVEL]
    : nomes;
}

/**
 * O valor com que a corretiva é procurada na multiseleção Área.
 *
 * @param corretiva - A corretiva.
 * @returns A área, ou `SEM_AREA`.
 */
export function valorDaArea(corretiva: Corretiva): string {
  return corretiva.area ?? SEM_AREA;
}

/**
 * O valor com que a corretiva é procurada na multiseleção Responsável.
 *
 * @param corretiva - A corretiva.
 * @returns O responsável, ou `SEM_RESPONSAVEL`.
 */
export function valorDoResponsavel(corretiva: Corretiva): string {
  return corretiva.responsavel ?? SEM_RESPONSAVEL;
}

/**
 * Diz se a próxima visita já passou.
 *
 * A de hoje não é vencida. O status não entra: uma rotina "Em andamento" com a
 * visita no passado também é vencida, como na origem.
 *
 * @param proximaVisita - `AAAA-MM-DD` ou `null`.
 * @param hojeIso - A data de hoje em hora local, de `hojeEmIso()`.
 * @returns `true` só para data presente e anterior a hoje.
 */
export function estaVencida(proximaVisita: string | null, hojeIso: string): boolean {
  return proximaVisita !== null && proximaVisita < hojeIso;
}

/**
 * O valor de ordenação da coluna "#".
 *
 * @param numero - O "#" como foi digitado.
 * @returns O número; `NaN` para texto que não é número, que a ordenação conta
 *   como vazio; `null` para o vazio.
 */
export function valorDoNumero(numero: string | null): number | null {
  return numero === null ? null : Number(numero);
}

/**
 * A situação de uma lista, a partir da consulta e da escolha da casa.
 *
 * @param consulta - O resultado do `useQuery`: `data` e `error`.
 * @param esperandoCasas - `true` enquanto a lista de casas não respondeu, e por
 *   isso ainda não se sabe que registros são da casa.
 * @returns `falhou` sem dado e com erro; `carregando` sem dado, ou com a casa
 *   ainda desconhecida; `pronta` no resto.
 */
export function situacaoDaConsulta(
  consulta: { data: unknown; error: unknown },
  esperandoCasas: boolean,
): SituacaoDaLista {
  if (consulta.data === undefined) return consulta.error === null ? 'carregando' : 'falhou';
  return esperandoCasas ? 'carregando' : 'pronta';
}

/**
 * O que a linha de vazio da tabela mostra.
 *
 * @param situacao - A situação da lista.
 * @param linhas - Quantas linhas sobraram depois dos filtros.
 * @returns `null` quando há linha para mostrar; senão a mensagem da linha.
 */
export function situacaoDaLinhaVazia(
  situacao: SituacaoDaLista,
  linhas: number,
): SituacaoDaLinhaVazia | null {
  if (situacao === 'carregando') return 'carregando';
  if (situacao === 'falhou') return 'falhou';
  return linhas === 0 ? 'vazia' : null;
}
